/* eslint-disable @typescript-eslint/no-explicit-any */
import { Transform, TransformationType } from 'class-transformer';
/**
 * Property decorator which handles the (de)serialization of a given property using the provided serializer
 * registration.
 * @param key The key of the property to serialize
 * @param selfType The type of the annotated field, a generated value
 * @param targetType The target serialization type, usually provided eg `Serialize(() => Foo)`,
 *                   falls back to selfType if not provided
 * @param serializerRegistrations The list of serializations within which a handler for targetType must be found
 */
export function SerializerTransformer(key, selfType, typeAlias, targetType, serializerRegistrations) {
    return Transform(({ value, type }) => {
        if (value === null || value === undefined) {
            return value;
        }
        const { serialize, deserialize, serializeArrayItems } = getTargetSerializer(serializerRegistrations, targetType, key);
        switch (type) {
            case TransformationType.CLASS_TO_PLAIN:
                if (!serialize) {
                    return value;
                }
                if (value instanceof Map) {
                    if (targetType !== selfType) {
                        const entries = Array.from(value.entries());
                        const serializedEntries = entries.map(([k, v]) => [k, serialize(v)]);
                        return Object.fromEntries(serializedEntries);
                    }
                    return Object.fromEntries(value.entries());
                }
                // By setting value to an array, it gets picked up by the array serialization method below
                if (value instanceof Set) {
                    return Array.from(value);
                }
                if (serializeArrayItems && Array.isArray(value)) {
                    return value.map(v => serialize(v));
                }
                return serialize(value);
            case TransformationType.PLAIN_TO_CLASS:
                if (!deserialize) {
                    return value;
                }
                if (Array.isArray(value) && selfType !== targetType) {
                    if (serializeArrayItems) {
                        const valueArray = value;
                        const array = valueArray.map(v => deserialize(v));
                        if (selfType === Set) {
                            return new Set(array);
                        }
                        return array;
                    }
                    if (selfType === Set) {
                        return new Set(value);
                    }
                    return value;
                }
                if ((selfType === Map || typeAlias === Map) && targetType !== Map) {
                    const entries = Object.entries(value).map(([k, v]) => [k, deserialize(v)]);
                    return new Map(entries);
                }
                return deserialize(value);
            default:
                throw new Error('ClassToClass not supported');
        }
    });
}
function listSerializers(serializerRegistrations) {
    return serializerRegistrations.map(r => r.type.name || r.type.description || 'No Name').join(';');
}
function getTargetSerializer(serializerRegistrations, targetType, key) {
    const serializerRegistration = serializerRegistrations.find(s => s.type === targetType);
    /**
     * class Foo {
     *   @Serialize()
     *   dog: LocalDate <-- dog is 'key' targetType is LocalDate
     * }
     */
    if (!serializerRegistration) {
        const errorMessage = `Serialization for type ${targetType} (key: '${key}') not implemented. ${serializerRegistrations.length} serializers are registered (${listSerializers(serializerRegistrations)}).` +
            (targetType === Array
                ? " Serialization of Array types should specify the type in the decorator, eg '@Serialize(() => ClassType)'"
                : '');
        throw new Error(errorMessage);
    }
    return serializerRegistration.serializer;
}
